import { useEffect, useState, SyntheticEvent } from "react";
import { useRecoilState } from "recoil";
import { RouteComponentProps } from "react-router-dom";
import {
  Alert,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Snackbar,
  Switch,
  TextField,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import {
  useTenantService,
  AlarmContactsReport,
  SiteAlarmContacts,
  TenantLifecycleStatus,
  SiteLifecycleStatus,
} from "../tenants/tenantService";
import { useSiteService } from "../sites/siteService";
import {
  smsRecipientsTenantsStatusFilterAtom,
  smsRecipientsSiteStatusFilterAtom,
} from "../shared/filterState";
import {
  AlarmContact,
  ServiceHoursDefinition,
  formatSendSmsHours,
  contactCategories,
  formatMessageCategories,
  DEFAULT_MESSAGE_CATEGORIES,
} from "../sites/alarmContact";
import SendSmsHoursDialog from "./SendSmsHoursDialog";
import MessageCategorySelector from "./MessageCategorySelector";

type ContactError = { name: string; phoneNumber: string; messageCategories: string };

const activeContactCount = (site: SiteAlarmContacts) =>
  (site.alarmContacts ?? []).filter((contact) => contact.isActive).length;

const validateContacts = (contacts: AlarmContact[]): ContactError[] =>
  contacts.map((contact, index) => {
    const phoneNumber = contact.phoneNumber?.trim() ?? "";
    const isDuplicate =
      !!phoneNumber &&
      contacts.findIndex((other) => (other.phoneNumber?.trim() ?? "") === phoneNumber) !== index;
    return {
      name: contact.name?.trim() ? "" : "Navn er påkrevd",
      phoneNumber: !phoneNumber
        ? "Telefonnummer er påkrevd"
        : isDuplicate
        ? "Telefonnummeret er allerede lagt til"
        : "",
      // Null betyr standardvalget; en tom liste valgt i skjemaet betyr at ingenting er valgt
      messageCategories:
        contact.isActive && contact.messageCategories != null && contact.messageCategories.length === 0
          ? "Velg minst én meldingstype"
          : "",
    };
  });

const receivesAlarms = (contacts: AlarmContact[]) =>
  contacts.some((contact) => contact.isActive && contactCategories(contact).includes("Alarm"));

export default function SmsRecipientsPage(props: RouteComponentProps) {
  const [report, setReport] = useState<AlarmContactsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const tenantService = useTenantService();
  const siteService = useSiteService();

  // Filter state
  const [selectedTenantStatuses, setSelectedTenantStatuses] = useRecoilState(smsRecipientsTenantsStatusFilterAtom);
  const [selectedSiteStatuses, setSelectedSiteStatuses] = useRecoilState(smsRecipientsSiteStatusFilterAtom);
  const [onlyWithoutContacts, setOnlyWithoutContacts] = useState(false);

  // Hvilke tenants som er apne. Kontrollert tilstand eid av siden, sa den overlever
  // refetch og filterendringer - tenantId er stabil pa tvers av begge.
  const [expandedTenantIds, setExpandedTenantIds] = useState<string[]>([]);

  // Site editing state
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editContacts, setEditContacts] = useState<AlarmContact[]>([]);
  const [hoursDialogIndex, setHoursDialogIndex] = useState<number | null>(null);

  // silent: hopp over helsides-spinneren. Uten den rives hele treet ned av render-guarden
  // nedenfor, og da mister vi scrollposisjon og apne accordions ved lagring.
  const loadReport = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const data = await tenantService.getAlarmContactsReport(
        selectedTenantStatuses.length > 0 ? selectedTenantStatuses : undefined,
        selectedSiteStatuses.length > 0 ? selectedSiteStatuses : undefined
      );
      setReport(data);
      setLoadError(null);
    } catch (err: any) {
      // En stille refresh skal ikke bytte ut siden med feiltekst - vi har data pa skjermen alt.
      if (silent) {
        setSaveError(err.message || "Failed to refresh SMS recipients report");
      } else {
        setLoadError(err.message || "Failed to load SMS recipients report");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantStatuses, selectedSiteStatuses]);

  const allTenantStatuses = Object.values(TenantLifecycleStatus);
  const allTenantStatusesSelected = selectedTenantStatuses.length === allTenantStatuses.length;
  const allSiteStatuses = Object.values(SiteLifecycleStatus);
  const allSiteStatusesSelected = selectedSiteStatuses.length === allSiteStatuses.length;

  const handleTenantStatusChange = (event: SelectChangeEvent<TenantLifecycleStatus[]>) => {
    const value = event.target.value;
    if (value.includes('__all__' as any)) {
      setSelectedTenantStatuses(allTenantStatusesSelected ? [] : allTenantStatuses);
    } else {
      setSelectedTenantStatuses(typeof value === 'string' ? value.split(',') as TenantLifecycleStatus[] : value);
    }
  };

  const handleSiteStatusChange = (event: SelectChangeEvent<SiteLifecycleStatus[]>) => {
    const value = event.target.value;
    if (value.includes('__all__' as any)) {
      setSelectedSiteStatuses(allSiteStatusesSelected ? [] : allSiteStatuses);
    } else {
      setSelectedSiteStatuses(typeof value === 'string' ? value.split(',') as SiteLifecycleStatus[] : value);
    }
  };

  const handleTenantExpandChange = (tenantId: string) =>
    (_event: SyntheticEvent, isExpanded: boolean) =>
      setExpandedTenantIds((prev) =>
        isExpanded ? [...prev, tenantId] : prev.filter((id) => id !== tenantId)
      );

  const startEditSite = (site: SiteAlarmContacts) => {
    setEditingSiteId(site.siteId);
    setEditContacts((site.alarmContacts ?? []).map((contact) => ({ ...contact })));
  };

  const cancelEditSite = () => {
    setEditingSiteId(null);
    setEditContacts([]);
    setHoursDialogIndex(null);
  };

  const updateContact = (index: number, patch: Partial<AlarmContact>) => {
    setEditContacts((prev) => prev.map((contact, i) => (i === index ? { ...contact, ...patch } : contact)));
  };

  const addContact = () => {
    setEditContacts((prev) => [
      ...prev,
      { name: "", phoneNumber: "", isActive: true, messageCategories: [...DEFAULT_MESSAGE_CATEGORIES] },
    ]);
  };

  const removeContact = (index: number) => {
    setEditContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const errors = validateContacts(editContacts);
  const hasErrors = errors.some((error) => error.name || error.phoneNumber || error.messageCategories);

  const saveAlarmContacts = async (siteId: string) => {
    if (hasErrors) return;
    setSaving(true);
    try {
      await siteService.postCommand(siteId, {
        type: "UpdateSiteAlarmContactsCommand",
        alarmContacts: editContacts,
      });
      setEditingSiteId(null);
      setEditContacts([]);
      setSaveSuccess(true);
      await loadReport({ silent: true });
    } catch (err: any) {
      setSaveError(err.message || "Failed to update SMS recipients");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box p={2}>
        <Typography color="error">{loadError}</Typography>
      </Box>
    );
  }

  if (!report) {
    return null;
  }

  // Klientside-filter: vis bare sites uten aktive mottakere (og skjul tenants som blir tomme)
  const tenants = report.tenantsAlarmContacts
    .map((tenant) => ({
      ...tenant,
      sites: onlyWithoutContacts
        ? tenant.sites.filter((site) => activeContactCount(site) === 0)
        : tenant.sites,
    }))
    .filter((tenant) => tenant.sites.length > 0);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          SMS Recipients
        </Typography>

        {/* Filter controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtrer på lifecycle status
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Tenant Status</InputLabel>
              <Select
                multiple
                value={selectedTenantStatuses}
                onChange={handleTenantStatusChange}
                input={<OutlinedInput label="Tenant Status" />}
                renderValue={(selected) => allTenantStatusesSelected ? 'Alle' : selected.join(', ')}
              >
                <MenuItem value={'__all__' as any}>
                  <Checkbox checked={allTenantStatusesSelected} indeterminate={selectedTenantStatuses.length > 0 && !allTenantStatusesSelected} />
                  <ListItemText primary="Velg alle" />
                </MenuItem>
                {allTenantStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedTenantStatuses.includes(status)} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Site Status</InputLabel>
              <Select
                multiple
                value={selectedSiteStatuses}
                onChange={handleSiteStatusChange}
                input={<OutlinedInput label="Site Status" />}
                renderValue={(selected) => allSiteStatusesSelected ? 'Alle' : selected.join(', ')}
              >
                <MenuItem value={'__all__' as any}>
                  <Checkbox checked={allSiteStatusesSelected} indeterminate={selectedSiteStatuses.length > 0 && !allSiteStatusesSelected} />
                  <ListItemText primary="Velg alle" />
                </MenuItem>
                {allSiteStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedSiteStatuses.includes(status)} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyWithoutContacts}
                onChange={(e) => setOnlyWithoutContacts(e.target.checked)}
              />
            }
            label="Vis bare sites uten aktive mottakere"
          />
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">
            {report.totalActiveContacts} aktive mottakere på {report.totalSites} sites
          </Typography>
          <Typography
            variant="body2"
            color={report.totalSitesWithoutActiveContacts > 0 ? "error.main" : "textSecondary"}
          >
            {report.totalSitesWithoutActiveContacts} sites uten aktive mottakere
          </Typography>
        </Paper>
      </Box>

      {tenants.map((tenant) => (
        <Accordion
          key={tenant.tenantId}
          expanded={expandedTenantIds.includes(tenant.tenantId)}
          onChange={handleTenantExpandChange(tenant.tenantId)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between" pr={2}>
              <Typography variant="h6">{tenant.tenantName}</Typography>
              <Box display="flex" gap={2}>
                <Chip
                  label={`${tenant.sites.length} sites`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {tenant.sitesWithoutActiveContacts > 0 ? (
                  <Chip
                    label={`${tenant.sitesWithoutActiveContacts} uten mottakere`}
                    size="small"
                    color="error"
                  />
                ) : (
                  <Chip label="OK" size="small" color="success" />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Site Name</TableCell>
                    <TableCell>SMS-mottakere</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenant.sites.map((site) => (
                    <TableRow key={site.siteId}>
                      {editingSiteId === site.siteId ? (
                        <TableCell colSpan={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            {site.siteName}
                          </Typography>
                          {editContacts.length > 0 && !receivesAlarms(editContacts) && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              <b>Ingen aktiv mottaker får alarmer.</b> Brannalarm, B-alarm og feil på sløyfe blir
                              ikke sendt på SMS fra dette sitet.
                            </Alert>
                          )}
                          {editContacts.map((contact, index) => (
                            <Box
                              key={index}
                              sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}
                            >
                            <Box
                              display="flex"
                              gap={1}
                              alignItems="flex-start"
                              flexWrap="wrap"
                              sx={{ mb: 1 }}
                            >
                              <TextField
                                label="Navn"
                                size="small"
                                value={contact.name ?? ""}
                                onChange={(e) => updateContact(index, { name: e.target.value })}
                                error={!!errors[index]?.name}
                                helperText={errors[index]?.name}
                                sx={{ width: 180 }}
                              />
                              <TextField
                                label="Telefonnummer"
                                size="small"
                                value={contact.phoneNumber ?? ""}
                                onChange={(e) => updateContact(index, { phoneNumber: e.target.value })}
                                error={!!errors[index]?.phoneNumber}
                                helperText={errors[index]?.phoneNumber}
                                sx={{ width: 200 }}
                              />
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Switch
                                  checked={contact.isActive}
                                  onChange={() => updateContact(index, { isActive: !contact.isActive })}
                                />
                                <Typography variant="caption" color="textSecondary">
                                  {contact.isActive ? "Aktiv" : "Inaktiv"}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <IconButton size="small" onClick={() => setHoursDialogIndex(index)}>
                                  <AccessAlarmIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" color="textSecondary">
                                  {formatSendSmsHours(contact.sendSmsHours)}
                                </Typography>
                              </Box>
                              <IconButton size="small" aria-label="Slett mottaker" onClick={() => removeContact(index)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <MessageCategorySelector
                              selected={contact.messageCategories ?? contactCategories(contact)}
                              disabled={!contact.isActive}
                              error={errors[index]?.messageCategories}
                              onChange={(selected) => updateContact(index, { messageCategories: selected })}
                            />
                            </Box>
                          ))}
                          <Box display="flex" gap={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                            <Button variant="outlined" size="small" onClick={addContact} disabled={saving}>
                              Legg til mottaker
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={saving || hasErrors}
                              onClick={() => saveAlarmContacts(site.siteId)}
                            >
                              {saving ? <CircularProgress size={20} /> : "Lagre"}
                            </Button>
                            <Button size="small" onClick={cancelEditSite} disabled={saving}>
                              Avbryt
                            </Button>
                          </Box>
                          {editContacts.length === 0 && (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                              Ingen mottakere - alarmer fra dette sitet vil ikke varsle noen.
                            </Typography>
                          )}
                          <SendSmsHoursDialog
                            open={hoursDialogIndex !== null}
                            contactLabel={
                              hoursDialogIndex !== null
                                ? editContacts[hoursDialogIndex]?.name ||
                                  editContacts[hoursDialogIndex]?.phoneNumber ||
                                  ""
                                : ""
                            }
                            value={
                              hoursDialogIndex !== null
                                ? editContacts[hoursDialogIndex]?.sendSmsHours ?? []
                                : []
                            }
                            onCancel={() => setHoursDialogIndex(null)}
                            onSave={(next: ServiceHoursDefinition[]) => {
                              if (hoursDialogIndex !== null) {
                                updateContact(hoursDialogIndex, { sendSmsHours: next });
                              }
                              setHoursDialogIndex(null);
                            }}
                          />
                        </TableCell>
                      ) : (
                        <>
                          <TableCell>{site.siteName}</TableCell>
                          <TableCell>
                            {(site.alarmContacts ?? []).length === 0 ? (
                              <Chip label="Ingen mottakere" size="small" color="error" />
                            ) : (
                              (site.alarmContacts ?? []).map((contact, index) => (
                                <Box
                                  key={index}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  sx={{ mb: 0.5, opacity: contact.isActive ? 1 : 0.5 }}
                                >
                                  <Chip
                                    label={`${contact.name || "(uten navn)"} · ${contact.phoneNumber}`}
                                    size="small"
                                    variant="outlined"
                                    color={contact.isActive ? "primary" : "default"}
                                  />
                                  <Typography variant="caption" color="textSecondary">
                                    {contact.isActive ? "" : "inaktiv · "}
                                    {formatSendSmsHours(contact.sendSmsHours)} · {formatMessageCategories(contact)}
                                  </Typography>
                                </Box>
                              ))
                            )}
                            {site.legacyPhoneNumber && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Legacy phoneNumber: {site.legacyPhoneNumber}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => startEditSite(site)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      <Paper sx={{ p: 2, mt: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Summary</strong>
                </TableCell>
                <TableCell align="right">Sites</TableCell>
                <TableCell align="right">Aktive mottakere</TableCell>
                <TableCell align="right">Uten aktive mottakere</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Grand Total</strong>
                </TableCell>
                <TableCell align="right">{report.totalSites}</TableCell>
                <TableCell align="right">{report.totalActiveContacts}</TableCell>
                <TableCell align="right">
                  <strong>{report.totalSitesWithoutActiveContacts}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          SMS-mottakere lagret
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveError(null)} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
